/**
 * CustomAvisoNovaVersao — o app percebe sozinho que saiu versão nova e oferece
 * o botão. Existe para que ninguém precise explicar "limpe o cache" ou "dá um
 * Ctrl+F5" por telefone.
 *
 * O PROBLEMA QUE ISTO RESOLVE: o deploy sobe certo, o servidor responde 200 e
 * correto, e o celular continua mostrando a tela antiga — porque o navegador
 * está servindo o `index.html` guardado, que aponta para o `/assets/` velho. O
 * Nginx daqui já manda `no-cache` no HTML, o que resolve a próxima abertura;
 * isto aqui cobre o buraco que o Nginx não alcança: **o app que já está aberto
 * na mão da pessoa**, e que não vai recarregar sozinho porque ninguém fecha o
 * aplicativo do celular. Instalado como app, então, é onde mais dói: a janela
 * fica aberta por dias.
 *
 * COMO SABE: o build carimba a mesma string em dois lugares — dentro do bundle
 * (`__VERSAO_APP__`) e num `versao.json` ao lado. Se o servidor devolve um
 * carimbo diferente do que está rodando aqui, existe versão nova. Nenhum
 * websocket, nenhum estado no servidor: um GET de 40 bytes.
 *
 * QUANDO PERGUNTA: ao abrir, ao voltar para o app (`visibilitychange` — o gesto
 * de quem larga o telefone e volta depois), e de tempos em tempos. Não pergunta
 * com a aba escondida: celular no bolso não precisa de rede.
 *
 * NÃO RECARREGA SOZINHO, de propósito. A pessoa pode estar no meio de uma OS,
 * com foto anexada e medida digitada — recarregar por baixo apagaria isso. A
 * decisão é dela; e, se ignorar, a próxima abertura do app já traz a versão
 * nova de qualquer jeito, pelo `no-cache` do Nginx.
 *
 * Portado do projeto irmão `webapp-rm-ops` (08/08/2026).
 */
import { useCallback, useEffect, useState } from 'react';

/** De quanto em quanto tempo perguntar, com o app aberto e visível. */
const INTERVALO = 10 * 60 * 1000;

export default function CustomAvisoNovaVersao() {
  const [temNova, setTemNova] = useState(false);

  const conferir = useCallback(async () => {
    // `no-store` no fetch além do cabeçalho do servidor: o objetivo aqui é
    // justamente furar cache, e este é o único lugar do app onde isso importa.
    try {
      const r = await fetch('/versao.json', { cache: 'no-store' });
      if (!r.ok) return;
      const { versao } = (await r.json()) as { versao?: string };
      if (versao && versao !== __VERSAO_APP__) setTemNova(true);
    } catch {
      // Sem rede ou servidor reiniciando: não é assunto de quem está usando.
    }
  }, []);

  useEffect(() => {
    // Em dev não existe deploy a detectar, e o `versao.json` nem é gerado.
    if (!import.meta.env.PROD) return;

    conferir();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') conferir();
    }, INTERVALO);
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') conferir();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [conferir]);

  if (!temNova) return null;

  return (
    <div
      className="position-fixed top-0 start-0 end-0 bg-theme-1 text-white shadow"
      style={{ zIndex: 1090, paddingTop: 'env(safe-area-inset-top)' }}
      role="status"
    >
      <div className="container-fluid py-2">
        <div className="row gx-2 align-items-center">
          <div className="col-auto">
            <i className="bi bi-arrow-clockwise"></i>
          </div>
          <div className="col small lh-sm">
            Tem uma versão nova do aplicativo.
            <span className="d-none d-sm-inline">
              {' '}
              Atualize quando terminar o que está fazendo.
            </span>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-sm btn-light"
              type="button"
              onClick={() => window.location.reload()}
            >
              Atualizar
            </button>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-sm btn-link text-white p-1"
              type="button"
              onClick={() => setTemNova(false)}
              aria-label="Agora não"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
